import {
  Entity,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { getEntityRelations } from '@backstage/plugin-catalog-react';

/**
 * A test over an already-loaded entity, used to decide whether a card or a tab
 * has anything to show (feature 0031).
 *
 * Predicates MUST stay synchronous. `EntityLayout.Route`'s `if` prop is typed
 * `(entity: Entity) => boolean` with no async variant, and an async guard would
 * render nothing until it settled — making cards pop in and shift the grid under
 * the reader after first paint.
 */
export type EntityPredicate = (entity: Entity) => boolean;

/** True when the entity declares at least one link. */
export const hasLinks: EntityPredicate = entity =>
  (entity.metadata.links?.length ?? 0) > 0;

/** True when the named annotation is present and non-empty. */
export const hasAnnotation =
  (name: string): EntityPredicate =>
  entity =>
    Boolean(entity.metadata.annotations?.[name]);

/**
 * True when the entity has TechDocs to show.
 *
 * Two annotations count. `backstage.io/techdocs-ref` points at the entity's own
 * docs source; `backstage.io/techdocs-entity` **delegates** to another entity's
 * docs, which several entities may share. Both make the Docs tab meaningful, so
 * checking only the first would hide a tab that has real content — the one
 * direction of error this feature must never take, since an unnecessary empty
 * card merely annoys while a missing tab hides information.
 */
export const hasTechDocs: EntityPredicate = entity =>
  hasAnnotation('backstage.io/techdocs-ref')(entity) ||
  hasAnnotation('backstage.io/techdocs-entity')(entity);

/**
 * True when the entity has at least one relation of `type` pointing at an entity
 * of `kind`.
 *
 * Every relation card in the catalog is one component parameterised by
 * `(relationType, entityKind)`, and this delegates to `getEntityRelations` — the
 * same public helper those cards use to resolve their contents. Sharing the helper
 * is the point: a card and the guard in front of it can never disagree about what
 * "has data" means, including how entity kinds are compared.
 */
export const hasRelationTo =
  (type: string, kind: string): EntityPredicate =>
  entity =>
    getEntityRelations(entity, type, { kind }).length > 0;

// One predicate per relation card, named after the card it guards.
//
// `hasSubcomponents` and `hasComponents` are deliberately the same test under two
// names: `EntityHasSubcomponentsCard` (on a Component) and `EntityHasComponentsCard`
// (on a System) both filter hasPart -> Component. Two names so each call site reads
// as the card it protects rather than as a relation tuple.
export const hasSubcomponents = hasRelationTo(RELATION_HAS_PART, 'Component');
export const hasComponents = hasSubcomponents;
export const hasApis = hasRelationTo(RELATION_HAS_PART, 'API');
export const hasResources = hasRelationTo(RELATION_HAS_PART, 'Resource');
export const hasSystems = hasRelationTo(RELATION_HAS_PART, 'System');
export const dependsOnComponents = hasRelationTo(
  RELATION_DEPENDS_ON,
  'Component',
);
export const dependsOnResources = hasRelationTo(
  RELATION_DEPENDS_ON,
  'Resource',
);
export const providesApis = hasRelationTo(RELATION_PROVIDES_API, 'API');
export const consumesApis = hasRelationTo(RELATION_CONSUMES_API, 'API');
export const hasApiProviders = hasRelationTo(
  RELATION_API_PROVIDED_BY,
  'Component',
);
export const hasApiConsumers = hasRelationTo(
  RELATION_API_CONSUMED_BY,
  'Component',
);
